// biome-ignore-all lint/style/useThrowOnlyError: historic

import { AuthConstants } from '@modules/auth-helpers';
import { auth } from '@modules/firebase';
import { logger } from '@modules/logger';
import {
  type AuthError,
  GoogleAuthProvider,
  OAuthProvider,
  type UserCredential,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getRedirectResult,
  isSignInWithEmailLink,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';

import { getMessageForError } from './util';

const LOG_MESSAGE = 'Firebase Auth operation complete.';
const LOG_OPERATION = {
  SIGN_IN: 'sign-in',
  SIGN_UP: 'sign-up',
  SIGN_OUT: 'sign-out',
  REDIRECT: 'redirect',
};
const LOG_METHOD = {
  CUSTOM_TOKEN: 'custom-token',
  LINK: 'link',
  OAUTH: 'oauth',
  PASSWORD: 'password',
  UNKNOWN: 'unknown',
};

type ValuesOf<T> = T[keyof T];

interface WithLoggingArgs<T> {
  action: Promise<T>;
  operation: ValuesOf<typeof LOG_OPERATION>;
  method: ValuesOf<typeof LOG_METHOD>;
  metadata?: Record<string, string | null>;
}

interface SignInWithRedirectArgs {
  email: string;
  providerId: 'google.com' | 'microsoft.com' | 'apple.com' | 'oidc.oauth2proxy';
}

interface SendSignInLinkToEmailArgs {
  email: string;
  /** URL you want to redirect back to. */
  redirectTo: string;
}

function isAuthError(error: unknown): error is AuthError {
  return (
    typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string'
  );
}

function isNewUser(user: UserCredential) {
  const additionalUserInfo = getAdditionalUserInfo(user);
  return additionalUserInfo?.isNewUser ?? false;
}

export class FirebaseAuthService {
  #log;

  constructor(log = logger.child({ class: 'FirebaseAuthService' })) {
    this.#log = log;
  }

  /**
   * Wraps a Firebase Auth operations with a consistent canonical log.
   * See: https://www.notion.so/blinq-me/Logging-for-observability-20569a35ad8f809db8f8c92d2d444bc4?source=copy_link
   */
  async #withLogging<T>({ action, operation, method, metadata = {} }: WithLoggingArgs<T>) {
    let error;
    try {
      return await action;
    } catch (err) {
      error = err;
      throw {
        code: isAuthError(err) ? err.code : 'unknown',
        message: isAuthError(err)
          ? getMessageForError(err)
          : 'An error occurred, please try again.',
      };
    } finally {
      if (!error) {
        this.#log.info(LOG_MESSAGE, { operation, method, ...metadata });
      } else {
        this.#log.error(LOG_MESSAGE, { operation, method, error, ...metadata });
      }
    }
  }

  async createUserWithEmailAndPassword(email: string, password: string) {
    const result = await this.#withLogging({
      action: createUserWithEmailAndPassword(auth, email, password),
      operation: LOG_OPERATION.SIGN_UP,
      method: LOG_METHOD.PASSWORD,
      metadata: { email },
    });
    return result;
  }

  async signInWithEmailAndPassword(email: string, password: string) {
    const result = await this.#withLogging({
      action: signInWithEmailAndPassword(auth, email, password),
      operation: LOG_OPERATION.SIGN_IN,
      method: LOG_METHOD.PASSWORD,
      metadata: { email },
    });
    return result;
  }

  async signInWithCustomToken(token: string) {
    let result, error;

    // TODO: Cannot use `#withLogging` here because we need to determine the `operation` based on the result.
    try {
      result = await signInWithCustomToken(auth, token);
      return result;
    } catch (err) {
      error = err;
      throw {
        code: isAuthError(err) ? err.code : 'unknown',
        message: isAuthError(err)
          ? getMessageForError(err)
          : 'An error occurred, please try again.',
      };
    } finally {
      const isNew = result ? isNewUser(result) : false;
      const operation = isNew ? LOG_OPERATION.SIGN_UP : LOG_OPERATION.SIGN_IN;
      const method = LOG_METHOD.CUSTOM_TOKEN;

      if (!error) {
        this.#log.info(LOG_MESSAGE, { operation, method, token });
      } else {
        this.#log.error(LOG_MESSAGE, { operation, method, error, token });
      }
    }
  }

  public isSignInWithEmailLink(link: string) {
    return isSignInWithEmailLink(auth, link);
  }

  async signInWithEmailLink(email: string, link: string) {
    let result, error;

    // TODO: Cannot use `#withLogging` here because we need to determine the `operation` based on the result.
    try {
      result = await signInWithEmailLink(auth, email, link);
      return result;
    } catch (err) {
      error = err;
      throw {
        code: isAuthError(err) ? err.code : 'unknown',
        message: isAuthError(err)
          ? getMessageForError(err)
          : 'An error occurred, please try again.',
      };
    } finally {
      const isNew = result ? isNewUser(result) : false;
      const operation = isNew ? LOG_OPERATION.SIGN_UP : LOG_OPERATION.SIGN_IN;
      const method = LOG_METHOD.LINK;

      if (!error) {
        this.#log.info(LOG_MESSAGE, { operation, method, email, link });
      } else {
        this.#log.error(LOG_MESSAGE, { operation, method, error, email, link });
      }
    }
  }

  /**
   * Automatically redirects the user to the specified OAuth provider for authentication. This can be a
   * first-time sign up or a sign-in for an existing user.
   */
  async signInWithRedirect({ email, providerId }: SignInWithRedirectArgs) {
    const provider =
      providerId === 'google.com' ? new GoogleAuthProvider() : new OAuthProvider(providerId);

    if (email) {
      provider.setCustomParameters({ login_hint: email });
    }

    localStorage.setItem(AuthConstants.LS_SSO_METHOD, providerId);

    // The redirect reloads the page and drops form state; keep the email so LoginForm can restore it if the
    // provider rejects the sign-in. Tab-scoped and cleared by LoginForm on return.
    if (email) {
      sessionStorage.setItem(AuthConstants.SS_SSO_EMAIL, email);
    } else {
      sessionStorage.removeItem(AuthConstants.SS_SSO_EMAIL);
    }

    // Used to set login loading state immediately upon return.
    localStorage.setItem(AuthConstants.LS_SSO_LOGIN, '1');

    await this.#withLogging({
      action: signInWithRedirect(auth, provider),
      operation: LOG_OPERATION.REDIRECT,
      method: LOG_METHOD.OAUTH,
      metadata: { email, providerId },
    });
  }

  /**
   * Opens a popup window for OAuth authentication instead of redirecting the current page.
   * Use this when the current page must preserve window.opener (e.g. HubSpot seamless install
   * popup flow where a full-page redirect severs the opener reference).
   */
  async signInWithPopup({ email, providerId }: SignInWithRedirectArgs) {
    const provider =
      providerId === 'google.com' ? new GoogleAuthProvider() : new OAuthProvider(providerId);

    if (email) {
      provider.setCustomParameters({ login_hint: email });
    }

    await this.#withLogging({
      action: signInWithPopup(auth, provider),
      operation: LOG_OPERATION.SIGN_IN,
      method: LOG_METHOD.OAUTH,
      metadata: { email, providerId },
    });
  }

  /**
   * Retrieves the signed-in user after a redirect to a social provider. For how a user is redirected to a
   * social provider, see `signInWithRedirect`.
   */
  async getRedirectResult() {
    let result, error;

    // These values are set in the `FirebaseAuthService.signInWithRedirect` method.
    const isSsoLogin = localStorage.getItem(AuthConstants.LS_SSO_LOGIN);
    const ssoMethod = localStorage.getItem(AuthConstants.LS_SSO_METHOD);

    // TODO: Cannot use `#withLogging` here because we need to determine the `operation` based on the result.
    try {
      result = await getRedirectResult(auth);
      return result;
    } catch (err) {
      error = err;
      throw {
        code: isAuthError(err) ? err.code : 'unknown',
        message: isAuthError(err)
          ? getMessageForError(err)
          : 'An error occurred, please try again.',
      };
    } finally {
      const isNew = result ? isNewUser(result) : false;
      const operation = isNew ? LOG_OPERATION.SIGN_UP : LOG_OPERATION.SIGN_IN;
      const method = LOG_METHOD.OAUTH;

      if (!error) {
        this.#log.info(LOG_MESSAGE, { operation, method, providerId: ssoMethod, isSsoLogin });
      } else {
        this.#log.error(LOG_MESSAGE, {
          operation,
          method,
          error,
          providerId: ssoMethod,
          isSsoLogin,
        });
      }
    }
  }

  async signOut() {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return;
    }

    const providerData = currentUser.providerData?.find(Boolean);
    const providerId = providerData?.providerId;

    if (!providerId) {
      this.#log.warn('Unknown provider for sign out. Using unknown method.', { user: currentUser });

      await this.#withLogging({
        action: signOut(auth),
        operation: 'sign-out',
        method: LOG_METHOD.UNKNOWN,
      });
      return;
    }

    if (providerId === 'password') {
      await this.#withLogging({
        action: signOut(auth),
        operation: 'sign-out',
        method: LOG_METHOD.PASSWORD,
      });
      return;
    }

    if (['google.com', 'microsoft.com', 'apple.com', 'oidc.oauth2proxy'].includes(providerId)) {
      await this.#withLogging({
        action: signOut(auth),
        operation: 'sign-out',
        method: LOG_METHOD.OAUTH,
        metadata: { providerId },
      });
      return;
    }

    this.#log.warn('Unknown provider for sign out. Using unknown method.', { user: currentUser });

    await this.#withLogging({
      action: signOut(auth),
      operation: 'sign-out',
      method: LOG_METHOD.UNKNOWN,
    });
  }
}

export const authService = new FirebaseAuthService();
